import type { FC } from 'react'

import { F0Box } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Hub } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  MyTrainingsDataCollectionDocument,
  MyTrainingsDataCollectionFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { statusFilterOptions } from 'modules/trainings/components/Shared/Filters/StatusFileterOptions'
import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'
import coursesBackground from 'modules/upselling_kit/lib/assets/blankSlate/locked-courses-bg.svg'

const ELLIPSIS_MAX_LENGTH = 32

const withEllipsis = (text: string) =>
  text.length > ELLIPSIS_MAX_LENGTH ? `${text.slice(0, ELLIPSIS_MAX_LENGTH).trimEnd()}...` : text

export const PastCoursesTab: FC<{
  targetAccessId: string
  canUpsellTrainings: boolean
}> = ({ targetAccessId, canUpsellTrainings }) => {
  const { getTrainingAttendanceTranslation, getDataCollectionTrainingAttendanceColor } =
    useTrainingsDatatable()

  const source = useGraphqlDataCollectionSource({
    document: MyTrainingsDataCollectionDocument,
    mapData: (data) => data?.trainings?.trainingsConnection?.nodes ?? [],
    mapPagination: (data) => ({
      total: data?.trainings?.trainingsConnection?.totalCount ?? 0,
    }),
    mapVariables: ({ filters, sortings }) => {
      const statusFilter = Array.isArray(filters.status) && filters.status.length > 0
      const filter = statusFilter
        ? {
            trainingAttendanceStatus: { in: filters.status },
          }
        : undefined

      return {
        accessId: targetAccessId,
        onlyAssigned: true,
        filter,
        sortOrder: sortings?.[0],
      }
    },
    filters: {
      status: {
        label: i18n.t('performance_process.targets.see_past_courses.status_column_name'),
        type: 'in',
        options: {
          cache: true,
          options: statusFilterOptions(),
        },
      },
    },
    search: {
      enabled: true,
    },
  })

  const emptyStates = {
    'no-data': {
      title: i18n.t('performance_process.targets.see_past_courses.empty_state.title'),
      description: i18n.t('performance_process.targets.see_past_courses.empty_state.description'),
      emoji: '📚',
    },
  }

  if (canUpsellTrainings) {
    return (
      <UpsellingKit.EmptyState
        moduleRequested={AvailableModules.trainings}
        requestSource="Performance - view past courses"
        requestReason={ValidReasons.MissingAddon}
        background={coursesBackground}
        title={i18n.t('upselling_kit.empty_state.title')}
        description={i18n.t('upselling_kit.empty_state.description')}
        variant="card"
      />
    )
  }

  return (
    <F0Box display="flex" flexDirection="column">
      <OneDataCollection
        id="performance_process/past_courses_tab/v1"
        source={source}
        emptyStates={emptyStates}
        visualizations={[
          {
            type: 'list',
            options: {
              fields: [
                {
                  label: i18n.t('performance_process.targets.see_past_courses.status_column_name'),
                  render: (item: MyTrainingsDataCollectionFragment) => ({
                    type: 'status',
                    value: {
                      label: getTrainingAttendanceTranslation(item.trainingAttendanceStatus),
                      status: getDataCollectionTrainingAttendanceColor(
                        item.trainingAttendanceStatus
                      ),
                    },
                  }),
                },
              ],
              itemDefinition: (item: MyTrainingsDataCollectionFragment) => {
                const competencies = item.competencies
                  .map((competency) => competency.name)
                  .join(', ')
                const competenciesText = withEllipsis(competencies)

                return {
                  title: withEllipsis(item.name),
                  description: competenciesText ? [competenciesText] : undefined,
                  avatar: {
                    type: 'icon',
                    icon: Hub,
                  },
                }
              },
            },
          },
        ]}
      />
    </F0Box>
  )
}
